import { IconButton, Stack, Box, Button, ButtonGroup } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { ZoomButtonStyle } from './styles';
import { ZoomDownIcon, ZoomExpandIcon, ZoomInIcon, ZoomOutIcon, ZoomUpIcon } from './icons/Icons';

const floorButtons = [
  { key: 'up', icon: ZoomUpIcon },
  { key: '3', color: '#676767' },
  { key: '2', color: '#667085' },
  { key: '1', color: '#676767' },
  { key: '0', color: '#fff', background: '#2055FF' },
  { key: '-1', color: '#646464' },
  { key: '-2', color: '#646464' },
  { key: 'down', icon: ZoomDownIcon },
];
const initialScale = 0.58; // Updated scale to 0.58
const initialPosition = { x: -213, y: -175 }; // Updated position

export function ZoomableMap({ selectedItem }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusOnMarker, setFocusOnMarker] = useState(false);
  const [scale, setScale] = useState(initialScale);
  const [position, setPosition] = useState({
    x: initialPosition.x || 0,
    y: initialPosition.y || 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [touchDistance, setTouchDistance] = useState<number | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Marker positioning
  const [markerPosition, setMarkerPosition] = useState({
    top: 1414,
    left: 2035,
  });

  useEffect(() => {
    if (selectedItem) {
      setFocusOnMarker(true);
    } else {
      setFocusOnMarker(false);
    }
  }, [selectedItem]);

  // Update scale when initialScale prop changes
  useEffect(() => {
    if (!focusOnMarker) {
      setScale(initialScale);
    }
  }, [focusOnMarker]);

  // Update position when initialPosition prop changes
  useEffect(() => {
    if (!focusOnMarker) {
      // Only update from control panel if not focusing on marker
      setPosition({
        x: initialPosition.x || 0,
        y: initialPosition.y || 0,
      });
    }

    // Mark initial load as complete once the image is loaded
    if (loaded && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [loaded, initialLoadComplete, focusOnMarker]);

  // Focus on marker when focusOnMarker prop changes
  useEffect(() => {
    if (focusOnMarker && containerRef.current && loaded) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      // Use a higher zoom level for the marker focus
      const markerZoom = 0.8;

      // Calculate position to center the marker in the viewport
      const markerX = containerWidth / 2 - markerPosition.left * markerZoom;
      const markerY = containerHeight / 2 - markerPosition.top * markerZoom;

      // Set the marker position immediately without animation
      setScale(markerZoom);
      setPosition({ x: markerX, y: markerY });
    }
  }, [focusOnMarker, loaded, markerPosition]);

  // Handle mouse down to start dragging or place marker
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  // Calculate distance between two touch points
  const getDistanceBetweenTouches = (e: React.TouchEvent | TouchEvent) => {
    if (e.touches.length < 2) return null;

    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch start for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Place marker at touch position
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      // Calculate the point on the image where the touch happened
      const imageX = (e.touches[0].clientX - containerRect.left - position.x) / scale;
      const imageY = (e.touches[0].clientY - containerRect.top - position.y) / scale;

      // Update marker position
      setMarkerPosition({
        top: Math.round(imageY),
        left: Math.round(imageX),
      });
    } else if (e.touches.length === 1) {
      // Single touch - start dragging
      setIsDragging(true);
      setStartPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
      setTouchDistance(null);
    } else if (e.touches.length === 2) {
      // Two touches - prepare for pinch zoom
      setIsDragging(false);
      const dist = getDistanceBetweenTouches(e);
      setTouchDistance(dist);
    }
  };

  // Get center point between two touches
  const getTouchCenter = (e: TouchEvent) => {
    if (e.touches.length < 2) return { x: 0, y: 0 };

    return {
      x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
      y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
    };
  };

  // Handle mouse move to drag the map - optimized for performance
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    // Use requestAnimationFrame for smoother dragging
    requestAnimationFrame(() => {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y,
      });
    });
  };

  // Handle touch move for mobile - optimized for performance
  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault(); // Prevent default to avoid browser gestures

    // Use requestAnimationFrame for smoother touch interactions
    requestAnimationFrame(() => {
      if (e.touches.length === 1 && isDragging) {
        // Single touch - drag
        setPosition({
          x: e.touches[0].clientX - startPos.x,
          y: e.touches[0].clientY - startPos.y,
        });
      } else if (e.touches.length === 2 && touchDistance) {
        // Two touches - pinch zoom
        const newDistance = getDistanceBetweenTouches(e);
        if (!newDistance) return;

        // Calculate new scale based on the change in distance
        const scaleFactor = newDistance / touchDistance;
        const newScale = Math.min(Math.max(scale * scaleFactor, 0.5), 5); // Limit scale between 0.5 and 5

        // Get the center point of the pinch
        const center = getTouchCenter(e);

        // Calculate how much the center point will move after scaling
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return;

        // Calculate the point on the image where the pinch center is
        const imageX = (center.x - containerRect.left - position.x) / scale;
        const imageY = (center.y - containerRect.top - position.y) / scale;

        // Calculate new position to keep the pinch center at the same place
        const newX = center.x - containerRect.left - imageX * newScale;
        const newY = center.y - containerRect.top - imageY * newScale;

        setScale(newScale);
        setPosition({ x: newX, y: newY });
        setTouchDistance(newDistance);
      }
    });
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
    setTouchDistance(null);
  };

  // Handle mouse wheel for zooming - optimized for performance
  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();

    // Use requestAnimationFrame for smoother wheel zooming
    requestAnimationFrame(() => {
      // Determine zoom direction
      const zoomIn = e.deltaY < 0;

      // Calculate new scale
      const scaleFactor = zoomIn ? 1.1 : 0.9;
      const newScale = Math.min(Math.max(scale * scaleFactor, 0.5), 5); // Limit scale between 0.5 and 5

      // Get mouse position relative to container
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      // Calculate the point on the image where the mouse is
      const mouseX = (e.clientX - containerRect.left - position.x) / scale;
      const mouseY = (e.clientY - containerRect.top - position.y) / scale;

      // Calculate new position to keep the mouse at the same place
      const newX = e.clientX - containerRect.left - mouseX * newScale;
      const newY = e.clientY - containerRect.top - mouseY * newScale;

      setScale(newScale);
      setPosition({ x: newX, y: newY });
    });
  };

  // Zoom in function - optimized for performance
  const zoomIn = () => {
    if (!containerRef.current) return;

    // Use requestAnimationFrame for smoother button zooming
    requestAnimationFrame(() => {
      const containerRect = containerRef.current!.getBoundingClientRect();
      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      // Calculate the point on the image that's currently at the center of the container
      const imageCenterX = (containerCenterX - position.x) / scale;
      const imageCenterY = (containerCenterY - position.y) / scale;

      // Calculate new scale
      const newScale = Math.min(scale + 0.3, 5);

      // Calculate new position to keep the same point at the center
      const newX = containerCenterX - imageCenterX * newScale;
      const newY = containerCenterY - imageCenterY * newScale;

      setScale(newScale);
      setPosition({ x: newX, y: newY });
    });
  };

  // Zoom out function - optimized for performance
  const zoomOut = () => {
    if (!containerRef.current) return;

    // Use requestAnimationFrame for smoother button zooming
    requestAnimationFrame(() => {
      const containerRect = containerRef.current!.getBoundingClientRect();
      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      // Calculate the point on the image that's currently at the center of the container
      const imageCenterX = (containerCenterX - position.x) / scale;
      const imageCenterY = (containerCenterY - position.y) / scale;

      // Calculate new scale
      const newScale = Math.max(scale - 0.3, 0.5);

      // Calculate new position to keep the same point at the center
      const newX = containerCenterX - imageCenterX * newScale;
      const newY = containerCenterY - imageCenterY * newScale;

      setScale(newScale);
      setPosition({ x: newX, y: newY });
    });
  };

  // Reset to initial view - centers on marker
  const resetView = () => {
    if (containerRef.current) {
      if (focusOnMarker) {
        // If in marker focus mode, center on marker
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;

        // Use a higher zoom level for the marker focus
        const markerZoom = 0.8;

        // Calculate position to center the marker in the viewport
        const markerX = containerWidth / 2 - markerPosition.left * markerZoom;
        const markerY = containerHeight / 2 - markerPosition.top * markerZoom;

        // Set the marker position immediately without animation
        setScale(markerZoom);
        setPosition({ x: markerX, y: markerY });
      } else {
        // Otherwise, reset to initial position
        setScale(initialScale);
        setPosition({
          x: initialPosition.x || 0,
          y: initialPosition.y || 0,
        });
      }
    }
  };

  // Add event listeners
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Mouse and touch events for dragging
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseup', handleMouseUp, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleMouseUp, { passive: true });

    // Wheel event for zooming
    container.addEventListener('wheel', handleWheel, { passive: false });

    // Handle window resize
    const handleResize = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerCenterX = containerRect.width / 2;
        const containerCenterY = containerRect.height / 2;

        // Calculate the point on the image that's currently at the center of the container
        const imageCenterX = (containerCenterX - position.x) / scale;
        const imageCenterY = (containerCenterY - position.y) / scale;

        // Calculate new position to keep the same point at the center
        const newX = containerCenterX - imageCenterX * scale;
        const newY = containerCenterY - imageCenterY * scale;

        setPosition({ x: newX, y: newY });
      }
    };

    window.addEventListener('resize', handleResize);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startPos, scale, position, touchDistance]);

  return (
    <Stack
      id="zoom-container"
      sx={{
        position: 'absolute',
        flex: 1,
        overflow: 'hidden',
        top: 69,
        left: 0,
        width: '100dvw',
        height: 'calc(100dvh - 69px)',
      }}
    >
      <Stack id="map-container" position="relative" flex={1} overflow="hidden" sx={{ background: '#101828' }}>
        {/* Map container */}
        <Stack
          ref={containerRef}
          sx={{
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            position: 'absolute',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div
            style={{
              transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
              transformOrigin: '0 0',
              opacity: loaded ? 1 : 0,
              willChange: 'transform',
            }}
          >
            <img
              src="/images/background.png"
              alt="Interactive floor map"
              className="max-w-none"
              onLoad={() => {
                setLoaded(true);
              }}
              draggable="false"
            />

            <Stack
              id="marker"
              position="absolute"
              style={{
                top: `${markerPosition.top}px`,
                left: `${markerPosition.left}px`,
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}
            >
              <Stack position="relative" mb="4px">
                <Stack
                  sx={{
                    backgroundColor: '#0F1A30',
                    color: 'white',
                    px: 4,
                    py: 1,
                    borderRadius: '9999px',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Heebo, sans-serif',
                  }}
                >
                  אתה כאן
                </Stack>

                <Stack
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 0,
                    width: 0,
                    height: 0,
                    transform: 'translateX(-50%) translateY(50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #0F1A30',
                  }}
                />
              </Stack>

              <Stack
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#2563EB',
                  border: '2px solid white',
                  boxShadow: 3,
                  position: 'relative',
                  alignSelf: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    backgroundColor: '#2563EB',
                    opacity: 0.7,
                    animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
                    '@keyframes ping': {
                      '0%': {
                        transform: 'scale(1)',
                        opacity: 0.7,
                      },
                      '75%, 100%': {
                        transform: 'scale(2)',
                        opacity: 0,
                      },
                    },
                  }}
                />
              </Stack>
            </Stack>
          </div>
        </Stack>

        <Stack
          id="zoom-controls"
          sx={{
            position: 'absolute',
            bottom: '50%',
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            zIndex: 20,
            transform: 'translateY(50%)',
          }}
        >
          <ButtonGroup
            orientation="vertical"
            variant="contained"
            sx={{
              background: 'linear-gradient(180deg, #E6E6E6 0%, rgba(230, 230, 230, 0.80) 42.5%, #E6E6E6 100%)',
              pointerEvents: 'none',
              cursor: 'pointer',
            }}
          >
            {floorButtons.map((fb) => {
              return (
                <Button key={fb.key} sx={{ ...ZoomButtonStyle, color: fb?.color, background: fb?.background }}>
                  {fb?.icon || fb.key}
                </Button>
              );
            })}
          </ButtonGroup>
          <ButtonGroup
            orientation="vertical"
            variant="contained"
            sx={{
              background: '#fff',
              '& .MuiButton-root:hover': {
                backgroundColor: '#fff',
                '& svg path': {
                  stroke: '#98A2B3',
                },
              },
            }}
          >
            <Button onClick={zoomIn} sx={ZoomButtonStyle} disabled={scale === 5}>
              {ZoomInIcon}
            </Button>
            <Button onClick={zoomOut} sx={ZoomButtonStyle} disabled={scale === 0.5}>
              {ZoomOutIcon}
            </Button>
          </ButtonGroup>
          <IconButton onClick={resetView} sx={ZoomButtonStyle}>
            {ZoomExpandIcon}
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  );
}
